import * as React from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { formatDate } from "@progress/kendo-intl";
import { process } from "@progress/kendo-data-query";

import LoadingIndicator from "../../components/LoadingIndicator";
import ErrorBanner from "../../components/ErrorBanner";
import SuccessBanner from "../../components/SuccessBanner";
import ReportExclusionsAddClientsModal from "./ReportExclusionsAddClientsModal";
import ReportExclusionsImportModal from "./ReportExclusionsImportModal";
import { useQueryGetOEPeriodList } from "../../data/services/openEnrollmentService";
import {
  useQueryGetReportExclusions,
  useMutatePostReportExclusionRemove,
} from "../../data/services/reportExclusionService";

/**
 * Report Exclusions admin page.
 *
 * Maintains the list of clients the admin dashboard chart counts and the
 * status tracking report leave out, for one OE period. Demo clients - any
 * client id below 1000 - are excluded by a server-side rule and are not
 * managed here, so the grid only ever shows the extra ids an admin added.
 */
const ReportExclusions = () => {
  const [selectedOEPeriod, setSelectedOEPeriod] = React.useState(null);
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [gridDataState, setGridDataState] = React.useState({
    skip: 0,
    take: 20,
  });

  const { isLoading: oePeriodsLoading, data: oePeriods } =
    useQueryGetOEPeriodList();

  const oePeriodId = selectedOEPeriod?.oePeriodId;

  const { isLoading: listLoading, data: listResponse } =
    useQueryGetReportExclusions(oePeriodId, includeInactive);

  const removeHandler = useMutatePostReportExclusionRemove();

  React.useEffect(() => {
    if (!oePeriodsLoading && oePeriods?.data?.length > 0 && !selectedOEPeriod) {
      setSelectedOEPeriod(oePeriods.data[0]);
    }
  }, [oePeriodsLoading, oePeriods, selectedOEPeriod]);

  const rows = listResponse?.data ?? [];

  // Only the active ids: a restored client should preview as "Exclude" on an
  // import, not as "Already Excluded".
  const activeClientIds = rows
    .filter((row) => row.active)
    .map((row) => row.clientId);

  const handleRemove = async (dataItem) => {
    try {
      await removeHandler.mutateAsync({
        oePeriodId,
        clientId: dataItem.clientId,
      });
    } catch {
      // Surfaced by <ErrorBanner handler={removeHandler} />.
    }
  };

  if (oePeriodsLoading) {
    return <LoadingIndicator />;
  }

  const dateCreatedCell = (props) => {
    // new Date(null) is the epoch and new Date(undefined) is Invalid Date, so
    // an absent value would otherwise render as 01/01/1970 or "Invalid Date".
    const raw = props.dataItem.dateCreated;
    const parsed = raw ? new Date(raw) : null;
    const isValid = parsed !== null && !Number.isNaN(parsed.getTime());

    return <td>{isValid ? formatDate(parsed, "MM/dd/yyyy") : ""}</td>;
  };

  const statusCell = (props) => (
    <td>{props.dataItem.active ? "Excluded" : "Restored"}</td>
  );

  const actionCell = (props) => {
    const dataItem = props.dataItem;

    if (!dataItem.active) {
      return <td />;
    }

    return (
      <td>
        <Button
          themeColor={"error"}
          fillMode={"flat"}
          onClick={() => handleRemove(dataItem)}
        >
          {"Restore"}
        </Button>
      </td>
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Report Exclusions</h2>
      <p>
        Clients on this list are left out of the Admin Dashboard chart counts
        and the Status Tracking Report for the selected OE period. Every other
        report is unaffected, and so are the notification screens.
      </p>
      <p data-testid="demo-client-note" style={{ color: "#666" }}>
        Demo clients &mdash; any client id below 1000 &mdash; are always
        excluded and do not need to be listed here.
      </p>

      <ErrorBanner handler={removeHandler} />
      <SuccessBanner
        handler={removeHandler}
        message={"Client restored to the reports."}
      />

      <div style={{ display: "flex", gap: "20px", alignItems: "flex-end" }}>
        <div>
          <label style={{ display: "block", fontWeight: 500 }}>OE Period</label>
          <DropDownList
            data={oePeriods?.data ?? []}
            ariaLabel={"OE Period"}
            textField={"oePeriodName"}
            dataItemKey={"oePeriodId"}
            value={selectedOEPeriod}
            onChange={(e) => setSelectedOEPeriod(e.target.value)}
            style={{ width: "260px" }}
          />
        </div>
        <div>
          <Checkbox
            label={"Show restored"}
            value={includeInactive}
            onChange={(e) => setIncludeInactive(e.value)}
          />
        </div>
      </div>

      <div style={{ marginTop: "20px" }}>
        {listLoading ? (
          <LoadingIndicator />
        ) : (
          <Grid
            data={process(rows, gridDataState)}
            {...gridDataState}
            onDataStateChange={(event) => setGridDataState(event.dataState)}
            sortable={true}
            filterable={true}
            pageable={{ pageSizes: [20, 50, 100] }}
          >
            <GridToolbar>
              <Button
                themeColor={"primary"}
                disabled={!oePeriodId}
                onClick={() => setShowAddModal(true)}
              >
                {"Exclude Clients"}
              </Button>
              <Button
                disabled={!oePeriodId}
                onClick={() => setShowImportModal(true)}
              >
                {"Import"}
              </Button>
            </GridToolbar>
            <Column field="clientId" title="Client Id" width={140} />
            <Column field="clientName" title="Client Name" />
            <Column
              field="dateCreated"
              title="Date Excluded"
              width={150}
              cell={dateCreatedCell}
            />
            <Column field="createdBy" title="Excluded By" width={180} />
            {includeInactive && (
              <Column
                field="active"
                title="Status"
                width={120}
                cell={statusCell}
              />
            )}
            <Column title="Actions" width={140} cell={actionCell} />
          </Grid>
        )}
      </div>

      {showAddModal && (
        <ReportExclusionsAddClientsModal
          oePeriodId={oePeriodId}
          onClose={() => setShowAddModal(false)}
          onSubmit={() => setShowAddModal(false)}
        />
      )}

      {showImportModal && (
        <ReportExclusionsImportModal
          oePeriodId={oePeriodId}
          currentClientIds={activeClientIds}
          onClose={() => setShowImportModal(false)}
          onSubmit={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
};

export default ReportExclusions;
